'use client';

import Link from 'next/link';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareFacebook, faYoutube, faInstagram, faSquareWhatsapp, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faMobile, faEnvelope, faLocationDot, faPeopleLine } from '@fortawesome/pro-solid-svg-icons';

type Props = {
  sectionName: string;
}

export default function Footer(props: Props) {
  const { sectionName } = props;


  const getDecoration = (sectionName: string): {
    line: string;
    position: string;
  } => {
    switch(sectionName) {
      case 'eventos':
        return {
          line: "/images/lines/redCirclePinkHook.png",
          position: 'left-0 h-15 rotate-y-90',
        };
      default:
        return {
          line: "/images/lines/longCircles.png",
          position: 'right-0 h-6 rotate-y-180',
        };
    }
  };

  return (
    <div className="relative bg-dark-light w-full py-8 text-white">
      <Image 
        src={getDecoration(sectionName).line} 
        width={439} 
        height={24} 
        className={`${getDecoration(sectionName).position} absolute top-0 w-auto`} 
        alt="Decoration"
        {...(getDecoration(sectionName).line?.endsWith('.svg') ? { unoptimized: true } : {})}
      />
      <div className="mt-4 flex flex-wrap justify-between gap-16 xl:gap-32 py-8 bigscreen-responsive">
        <div className="text-center xl:text-left flex-1 max-w-sm">
          <h1 className="font-bold text-xl">Amura Centro Culinario</h1>
          <p className="font-thin text-sm mt-4">La escuela de cocina que está revolucionando la forma de enseñar gastronomía</p>
          <div className="flex mt-8 gap-4 justify-center items-center xl:justify-start">
            <Link href="https://www.facebook.com/amura.cr" target='_blank' aria-label="Facebook"><FontAwesomeIcon icon={faSquareFacebook} className="text-[30px] mb-4 hover:text-[#E5E7EB]/65"/></Link>
            <Link href="https://youtube.com/@amuracentroculinario1581" target='_blank' aria-label="YouTube"><FontAwesomeIcon icon={faYoutube} className="text-[30px] mb-4 hover:text-[#E5E7EB]/65"/></Link>
            <Link href="https://www.instagram.com/amura.cr" target='_blank' aria-label="Instagram"><FontAwesomeIcon icon={faInstagram} className="text-[30px] mb-4 hover:text-[#E5E7EB]/65"/></Link>
            <Link href="https://www.tiktok.com/@amuracr" target='_blank' aria-label="Tiktok"><FontAwesomeIcon icon={faTiktok} className="text-[24px] mb-4 hover:text-[#E5E7EB]/65"/></Link>
          </div>
        </div>
        <div className="mb-4 text-center xl:text-left mx-auto xl:mx-0">
          <h1 className="font-bold">Secciones</h1>
          <div className="flex justify-center xl:justify-start gap-7 text-left">
            <ul className="mt-4 font-thin text-sm mb-6">
              <li className="mb-6 hover:text-[#E5E7EB]/65"><Link href='/programas'>Programas Técnicos</Link></li>
              <li className="mb-6 hover:text-[#E5E7EB]/65"><Link href='/vida-estudiantil'>Vida Estudiantil</Link></li>
              <li className="mb-6 hover:text-[#E5E7EB]/65"><Link href='/eventos'>Eventos & Experiencias</Link></li>
              <li className="hidden hover:text-[#E5E7EB]/65"><Link href="/blog">Blog</Link></li>
            </ul>
            <ul className="mt-4 font-thin text-sm">
              <li className="mb-6 hover:text-[#E5E7EB]/65"><Link href="/acerca">Acerca</Link></li>
              <li className="mb-6 hover:text-[#E5E7EB]/65"><Link href="/instalaciones">Instalaciones</Link></li>
              <li className="hover:text-[#E5E7EB]/65"><Link href="https://tour.amura.cr" target='_blank'>Tour Virtual</Link></li>
            </ul>
          </div>
        </div>
        <div className="flex flex-wrap justify-center text-center max-w-sm xl:justify-start">
          <h1 className="font-bold text-2xl">¡Hablemos!</h1>
          <div className="mt-4 xl:text-left xl:items-center font-thin text-sm">
            <div className="mb-6 hover:text-[#E5E7EB]/65">
              <Link href="tel:40002433">
                <FontAwesomeIcon icon={faMobile} className="text-[18px] mr-2 opacity-40" /> 
                4000-CHEF (2433)
              </Link>
            </div>
            <div className="mb-6 hover:text-[#E5E7EB]/65">
              <Link href='https://wa.me/50662236864' target='_blank'>
                <FontAwesomeIcon icon={faSquareWhatsapp} className="text-[18px] mr-2 opacity-40" />
                +506 6223-6864
              </Link>
            </div>
            <div className="mb-6 hover:text-[#E5E7EB]/65">
              <Link href="mailto:info@amura.cr">
                <FontAwesomeIcon icon={faEnvelope} className="text-[18px] mr-2 opacity-40" /> 
                info@amura.cr
              </Link>
            </div>
            <div className="">
              <Link href='https://www.google.com/maps?rlz=1C5CHFA_enCR992CR992&gs_lcrp=EgZjaHJvbWUqEggBEC4YFBivARjHARiHAhiABDIGCAAQRRg5MhIIARAuGBQYrwEYxwEYhwIYgAQyBwgCEAAYgAQyCAgDEAAYFhgeMggIBBAAGBYYHjIGCAUQRRg8MgYIBhBFGDwyBggHEEUYPNIBCDUzMjlqMGo3qAIAsAIA&um=1&ie=UTF-8&fb=1&gl=cr&sa=X&geocode=KWWLV7y_5aCPMfrfK633C4Pr&daddr=400+metros+al+este+del+cruce+de+Llorente+de,+San+Jos%C3%A9+Province,+Tibas' target='_blank' className="hover:text-[#E5E7EB]/65 flex items-center gap-2 justify-center xl:justify-start">
                <FontAwesomeIcon icon={faLocationDot} className="text-[18px] mr-2 opacity-40" />
                400 metros al este del cruce de Llorente de Tibás. San José, Costa Rica. Contiguo a Grupo Nación.
              </Link>
            </div>
            <span className="w-8 rounded-full h-1 bg-white/10 inline-block mb-3 mt-4" />
            <div className="flex items-center gap-2 justify-center xl:justify-start">
              <FontAwesomeIcon icon={faPeopleLine} className="text-[18px] opacity-40" /> 
              <div>
                <p className="font-bold">Eventos corporativos:</p>
                <div className="hover:text-[#E5E7EB]/65 text-sm">
                  <Link href="https://wa.me/50689113099" target='_blank'>
                    +506 8911-3099
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-[#F8A35A] flex flex-col gap-4 mt-4 py-4 px-4 xl:py-0 xl:mx-8">
        <div className="mt-2 text-center items-center xl:flex justify-between">
          <p className="font-thin text-xs">©{ new Date().getFullYear() } Amura Centro Culinario S.R.L. • <Link href="/politica-de-privacidad" className='hover:opacity-40'>Política de privacidad</Link></p>
          <Link href="https://elcomal.amura.cr" target='_blank' className="font-thin text-xs hover:text-[#E5E7EB]/65">Ingresar a el comal »</Link>
        </div>
      </div>
    </div>
  )
}